import { AxiosResponse } from 'axios';
import { API_CONFIG_V2 } from '../../config/api.v2.config';
import {
  Block,
  ReissueRequest,
  ReissueResponse,
  SendOTPRequest,
  VerifyOTPRequest,
  VerifyOTPResponse,
} from '../../types/api.v2.types';
import { getTokens } from '../../utils/util';
import { reportTokenSuccess, reportTokenDebug } from '../../utils/errorReporting';
import { handleApiCallV2 } from './config';
import i18n from '../../i18n';

const saveTokens = (serviceTokenDto: { accessToken: string; refreshToken: string }) => {
  if (!serviceTokenDto.accessToken || !serviceTokenDto.refreshToken) {
    console.error('Invalid tokens received from server:', serviceTokenDto);
    throw new Error('서버에서 유효하지 않은 토큰을 받았습니다.');
  }

  localStorage.setItem('accessToken', serviceTokenDto.accessToken);
  localStorage.setItem('refreshToken', serviceTokenDto.refreshToken);

  reportTokenSuccess('토큰 저장 완료', {
    accessTokenLength: serviceTokenDto.accessToken.length,
    refreshTokenLength: serviceTokenDto.refreshToken.length,
    timestamp: new Date().toISOString(),
  });
};

export const sendOTP = async (email: string): Promise<AxiosResponse> => {
  const data: SendOTPRequest = { email };
  return handleApiCallV2<SendOTPRequest, AxiosResponse>(
    API_CONFIG_V2.ENDPOINTS.SEND_OTP,
    data,
    'POST'
  );
};

export const verifyOTP = async (email: string, pin: string): Promise<VerifyOTPResponse> => {
  const data: VerifyOTPRequest = { email, pin, language: i18n.language.split('-')[0] };
  return handleApiCallV2<VerifyOTPRequest, VerifyOTPResponse>(
    API_CONFIG_V2.ENDPOINTS.VERIFY_OTP,
    data,
    'POST'
  ).then((response) => {
    saveTokens(response.data.serviceTokenDto);
    return response.data;
  });
};

export const checkCert = async (): Promise<Block> => {
  const params = { language: i18n.language.split('-')[0] };
  return handleApiCallV2<typeof params, Block>(API_CONFIG_V2.ENDPOINTS.CERT, params, 'GET', true).then(
    (response) => {
      return response.data;
    }
  );
};

export const reissueToken = async (): Promise<ReissueResponse> => {
  const data: ReissueRequest = getTokens();

  if (!data.accessToken || !data.refreshToken) {
    throw new Error('저장된 토큰이 없습니다.');
  }

  reportTokenDebug('reissueToken 디버그', {
    data,
    accessTokenType: typeof data.accessToken,
    refreshTokenType: typeof data.refreshToken,
    accessTokenValue: data.accessToken,
    refreshTokenValue: data.refreshToken,
    accessTokenLength: data.accessToken?.length,
    refreshTokenLength: data.refreshToken?.length,
  });

  reportTokenDebug('토큰 재발급 요청', {
    hasAccessToken: !!data.accessToken,
    hasRefreshToken: !!data.refreshToken,
    accessTokenLength: data.accessToken?.length || null,
    refreshTokenLength: data.refreshToken?.length || null,
  });

  return handleApiCallV2<ReissueRequest, ReissueResponse>(
    API_CONFIG_V2.ENDPOINTS.REISSUE,
    data,
    'POST'
  ).then((response) => {
    reportTokenSuccess('새 토큰 수신 완료', {
      accessTokenLength: response.data.accessToken.length,
      refreshTokenLength: response.data.refreshToken.length,
      timestamp: new Date().toISOString(),
    });

    saveTokens({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    });

    const savedTokens = getTokens();
    reportTokenDebug('토큰 저장 후 확인', {
      accessTokenExists: !!savedTokens.accessToken,
      refreshTokenExists: !!savedTokens.refreshToken,
    });

    return response.data;
  });
};

export const getMainBlock = async (): Promise<Block> => {
  return handleApiCallV2<null, Block>(API_CONFIG_V2.ENDPOINTS.GET_MAIN_BLOCK, null, 'GET').then(
    (response) => response.data
  );
};
